import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import HistoryIcon from '@mui/icons-material/History'
import SearchIcon from '@mui/icons-material/Search'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { HOLD_COMMENT_MAX } from '../../../../../api/holdallocation/hold-allocation-api'

function HoldDetailsTableV2({
  holds,
  onRelease,
  onReleaseAll,
  onReapply = undefined,
  isSubmitting,
}) {
  const [comments, setComments] = useState({})
  const [filterField, setFilterField] = useState('createdOn')
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyHoldCode, setHistoryHoldCode] = useState('ALL')
  const [historyQuery, setHistoryQuery] = useState('')
  const [historyPage, setHistoryPage] = useState(0)
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(5)
  // Re-apply confirm dialog state
  const [reapplyTarget, setReapplyTarget] = useState(null)
  const [reapplyComment, setReapplyComment] = useState('')

  const openHolds = holds.filter((h) => !h.released)
  const releasableHolds = openHolds.filter((h) => h.releasable)
  const allReleasableReleased = releasableHolds.length === 0 && holds.some((h) => h.released)
  const historyHoldCodes = useMemo(() => {
    const unique = Array.from(new Set(holds.map((h) => h.holdCode).filter(Boolean)))
    unique.sort((a, b) => a.localeCompare(b))
    return unique
  }, [holds])

  const historyRows = useMemo(() => {
    // Derive event rows from the current hold state. The live API exposes up
    // to three persisted markers per hold:
    //   - appliedAt / heldBy              (immutable original apply)
    //   - lastReappliedAt / lastReappliedBy (most recent re-apply, if any)
    //   - releasedAt / releasedBy / releaseComment (most recent release,
    //     only when hold.released === true)
    // That means at most 3 history rows can be reconstructed per hold.
    // Any release that was subsequently overwritten by a re-apply is lost at
    // the backend; we document this limitation rather than pretend otherwise.
    const rows = []
    holds.forEach((hold) => {
      // 1) Applied (always)
      rows.push({
        key: `${hold.holdId}-applied`,
        holdId: hold.holdId,
        holdCode: hold.holdCode || '—',
        description: hold.description || '—',
        appliedTo: hold.appliedToDisplay || hold.appliedTo || '—',
        time: hold.appliedAt || null,
        holdBy: hold.heldBy || '—',
        comment: '—',
        holdType: 'Applied',
        hold,
      })

      // 2) Re-Applied (if ever re-applied — independent of current released state)
      if (hold.lastReappliedAt) {
        rows.push({
          key: `${hold.holdId}-reapplied`,
          holdId: hold.holdId,
          holdCode: hold.holdCode || '—',
          description: hold.description || '—',
          appliedTo: hold.appliedToDisplay || hold.appliedTo || '—',
          time: hold.lastReappliedAt,
          holdBy: hold.lastReappliedBy || '—',
          // Backend doesn't persist applyComment on the hold record.
          comment: '—',
          holdType: 'Re-Applied',
          hold,
        })
      }

      // 3) Released (only when currently released — backend wipes releasedAt
      //    on re-apply, so a release followed by a re-apply leaves no trace)
      if (hold.released && hold.releasedAt) {
        rows.push({
          key: `${hold.holdId}-released`,
          holdId: hold.holdId,
          holdCode: hold.holdCode || '—',
          description: hold.description || '—',
          appliedTo: hold.appliedToDisplay || hold.appliedTo || '—',
          time: hold.releasedAt,
          holdBy: hold.releasedBy || '—',
          comment: hold.releaseComment || '—',
          holdType: 'Released',
          hold,
        })
      }
    })

    const term = historyQuery.trim().toLowerCase()
    const filtered = rows.filter((row) => {
      if (historyHoldCode !== 'ALL' && row.holdCode !== historyHoldCode) return false
      if (!term) return true
      return [
        row.holdCode,
        row.description,
        row.appliedTo,
        row.holdBy,
        row.comment,
        row.holdType,
      ].some((value) =>
        String(value || '')
          .toLowerCase()
          .includes(term),
      )
    })

    // Chronological ascending: Applied -> Re-Applied -> Released in natural
    // time order. Ties resolved by a stable Applied < Re-Applied < Released
    // ordering so a same-millisecond backfill still reads correctly.
    const typeRank = { Applied: 0, 'Re-Applied': 1, Released: 2 }
    filtered.sort((a, b) => {
      const ta = a.time ? new Date(a.time).getTime() : Number.MAX_SAFE_INTEGER
      const tb = b.time ? new Date(b.time).getTime() : Number.MAX_SAFE_INTEGER
      if (ta !== tb) return ta - tb
      return (typeRank[a.holdType] ?? 99) - (typeRank[b.holdType] ?? 99)
    })
    return filtered
  }, [holds, historyHoldCode, historyQuery])

  // Reset page when filters change or total rows shrink
  const totalHistoryRows = historyRows.length
  const totalHistoryPages = Math.max(1, Math.ceil(totalHistoryRows / historyRowsPerPage))
  const safePage = Math.min(historyPage, totalHistoryPages - 1)
  const pageStart = safePage * historyRowsPerPage
  const pageEnd = Math.min(pageStart + historyRowsPerPage, totalHistoryRows)
  const paginatedHistoryRows = historyRows.slice(pageStart, pageEnd)

  // Opens the confirm dialog. Actual API call happens in confirmReapply.
  const handleReapply = (row) => {
    setReapplyTarget(row.hold)
    setReapplyComment('')
  }

  const cancelReapply = () => {
    setReapplyTarget(null)
    setReapplyComment('')
  }

  const confirmReapply = () => {
    const target = reapplyTarget
    if (!target || !onReapply) return
    onReapply(target, reapplyComment.trim())
    setReapplyTarget(null)
    setReapplyComment('')
    // Close the history dialog so the parent's success/error snackbar is visible
    setHistoryOpen(false)
  }

  const handleCommentChange = (holdId, value) => {
    setComments((prev) => ({ ...prev, [holdId]: value }))
  }

  const isCommentValid = (text) => {
    if (!text) return false
    const trimmed = text.trim()
    return trimmed.length > 0 && trimmed.length <= HOLD_COMMENT_MAX
  }

  const handleRelease = (hold) => {
    const text = (comments[hold.holdId] || '').trim()
    if (!isCommentValid(text)) return
    onRelease(hold, text)
  }

  const handleReleaseAll = () => {
    // Concatenate then truncate to the server-side cap so the joined string
    // never exceeds 36 chars (the server still validates).
    const joined = releasableHolds
      .map((h) => (comments[h.holdId] || '').trim())
      .filter(Boolean)
      .join('; ')
    const commentStr = joined.slice(0, HOLD_COMMENT_MAX)
    if (!isCommentValid(commentStr)) return
    onReleaseAll(commentStr)
  }

  // Release-all is only enabled when every releasable hold has a valid comment
  // and the joined batch comment is itself within range.
  const releaseAllEnabled =
    releasableHolds.length > 0 && releasableHolds.every((h) => isCommentValid(comments[h.holdId]))

  const formatTime = (dt) => {
    if (!dt) return '—'
    const d = new Date(dt)
    return `${d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })}, ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
  }

  return (
    <Box>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ fontFamily: 'SanomatMedium', fontSize: '1.3rem', color: '#1a8a6a' }}>
          Hold Details
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Filter dropdown (decorative) */}
          <Select
            size='small'
            value={filterField}
            onChange={(e) => setFilterField(e.target.value)}
            sx={{
              fontFamily: 'AtlasGroteskRegular',
              fontSize: '0.82rem',
              minWidth: 120,
              height: 36,
            }}
          >
            <MenuItem value='createdOn'>Created on</MenuItem>
            <MenuItem value='status'>Status</MenuItem>
          </Select>
          {/* Search (decorative) */}
          <TextField
            size='small'
            placeholder='MM/DD/YYYY'
            sx={{
              width: 160,
              '& .MuiInputBase-input': {
                fontFamily: 'AtlasGroteskRegular',
                fontSize: '0.82rem',
                height: 20,
              },
            }}
          />
          {/* Release All or Released indicator */}
          {allReleasableReleased ? (
            <Button
              variant='contained'
              size='small'
              disabled
              sx={{
                fontFamily: 'AtlasGroteskRegular',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                backgroundColor: '#bbb',
                color: '#fff',
                '&.Mui-disabled': { backgroundColor: '#ccc', color: '#fff' },
              }}
            >
              Released
            </Button>
          ) : releasableHolds.length > 1 ? (
            <Button
              variant='contained'
              size='small'
              onClick={handleReleaseAll}
              disabled={isSubmitting || !releaseAllEnabled}
              sx={{
                fontFamily: 'AtlasGroteskRegular',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                backgroundColor: '#1a6b52',
                color: '#fff',
                '&:hover': { backgroundColor: '#145a4b' },
                '&.Mui-disabled': { backgroundColor: '#cccccc', color: '#ffffff' },
              }}
            >
              Release All
            </Button>
          ) : null}
          {/* History button (decorative) */}
          <Button
            variant='outlined'
            size='small'
            startIcon={<HistoryIcon sx={{ fontSize: 16 }} />}
            onClick={() => setHistoryOpen(true)}
            sx={{
              fontFamily: 'AtlasGroteskRegular',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              borderColor: '#1a8a6a',
              color: '#1a8a6a',
              height: 36,
            }}
          >
            History
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Box
        sx={{
          backgroundColor: '#fff',
          border: '1px solid #e7e7e7',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '100px 1fr 130px 180px 1fr 130px',
            gap: 1,
            px: 2,
            py: 1.5,
            backgroundColor: '#f8f8f8',
            borderBottom: '1px solid #e7e7e7',
          }}
        >
          {[
            'Hold Code',
            'Hold Description',
            'Applied To',
            'Time',
            openHolds.length > 1 ? 'Comment*' : 'Hold By',
            'Action',
          ].map((h) => (
            <Typography
              key={h}
              sx={{
                fontFamily: 'AtlasGroteskRegular',
                fontSize: '0.78rem',
                fontWeight: 700,
                color: '#555',
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {/* Rows */}
        {openHolds.map((hold) => (
          <Box
            key={hold.holdId}
            sx={{
              display: 'grid',
              gridTemplateColumns: '100px 1fr 130px 180px 1fr 130px',
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #f0f0f0',
              alignItems: 'center',
              '&:last-child': { borderBottom: 'none' },
            }}
          >
            {/* Hold Code */}
            <Typography
              sx={{
                fontFamily: 'AtlasGroteskRegular',
                fontSize: '0.85rem',
                fontWeight: 700,
                color: '#1a8a6a',
              }}
            >
              {hold.holdCode}
            </Typography>

            {/* Hold Description */}
            <Typography
              sx={{ fontFamily: 'AtlasGroteskRegular', fontSize: '0.82rem', color: '#262626' }}
            >
              {hold.description}
            </Typography>

            {/* Applied To */}
            <Typography
              sx={{ fontFamily: 'AtlasGroteskRegular', fontSize: '0.82rem', color: '#262626' }}
            >
              {hold.appliedToDisplay || hold.appliedTo}
            </Typography>

            {/* Time */}
            <Typography
              sx={{ fontFamily: 'AtlasGroteskRegular', fontSize: '0.82rem', color: '#262626' }}
            >
              {formatTime(hold.appliedAt)}
            </Typography>

            {/* Comment / Hold By */}
            <Box>
              {hold.released ? (
                <Typography
                  sx={{ fontFamily: 'AtlasGroteskRegular', fontSize: '0.82rem', color: '#262626' }}
                >
                  {hold.lastReappliedBy || hold.releasedBy || '—'}
                </Typography>
              ) : (
                <TextField
                  size='small'
                  placeholder='Add Comment...'
                  value={comments[hold.holdId] || ''}
                  onChange={(e) => handleCommentChange(hold.holdId, e.target.value)}
                  disabled={!hold.releasable || isSubmitting}
                  inputProps={{ maxLength: HOLD_COMMENT_MAX }}
                  helperText={
                    (comments[hold.holdId] || '').length >= HOLD_COMMENT_MAX - 5
                      ? `${(comments[hold.holdId] || '').length}/${HOLD_COMMENT_MAX}`
                      : ' '
                  }
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-input': {
                      fontFamily: 'AtlasGroteskRegular',
                      fontSize: '0.82rem',
                      py: 0.75,
                    },
                    '& .MuiFormHelperText-root': {
                      fontFamily: 'AtlasGroteskRegular',
                      fontSize: '0.7rem',
                      m: 0,
                      mt: 0.25,
                      lineHeight: 1,
                      minHeight: '0.7rem',
                    },
                  }}
                />
              )}
            </Box>

            {/* Action */}
            <Box>
              {hold.released ? (
                <Button
                  variant='outlined'
                  size='small'
                  disabled
                  sx={{
                    fontFamily: 'AtlasGroteskRegular',
                    textTransform: 'uppercase',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    borderColor: '#2db47d',
                    color: '#2db47d',
                    '&.Mui-disabled': { borderColor: '#2db47d', color: '#2db47d', opacity: 0.9 },
                  }}
                >
                  Released
                </Button>
              ) : hold.releasable ? (
                <Button
                  variant='contained'
                  size='small'
                  onClick={() => handleRelease(hold)}
                  disabled={isSubmitting || !isCommentValid(comments[hold.holdId])}
                  sx={{
                    fontFamily: 'AtlasGroteskRegular',
                    textTransform: 'uppercase',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    backgroundColor: '#1a6b52',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#145a4b' },
                    '&.Mui-disabled': { backgroundColor: '#cccccc', color: '#ffffff' },
                  }}
                >
                  Release
                </Button>
              ) : (
                <Button
                  variant='outlined'
                  size='small'
                  disabled
                  startIcon={<LockIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    fontFamily: 'AtlasGroteskRegular',
                    textTransform: 'uppercase',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    borderColor: '#ccc',
                    color: '#999',
                    '&.Mui-disabled': { borderColor: '#ccc', color: '#999' },
                  }}
                >
                  Locked
                </Button>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Re-apply confirmation dialog */}
      <Dialog open={!!reapplyTarget} onClose={cancelReapply} fullWidth maxWidth='xs'>
        <DialogTitle
          sx={{ fontFamily: 'SanomatMedium', fontSize: '1.3rem', color: '#1a8a6a', pb: 1 }}
        >
          Re-Apply Hold
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography
            sx={{ fontFamily: 'AtlasGroteskRegular', fontSize: '0.88rem', color: '#262626', mb: 2 }}
          >
            Re-apply hold <strong>{reapplyTarget?.holdCode}</strong>
            {reapplyTarget?.description ? ` — ${reapplyTarget.description}` : ''}?
          </Typography>
          <TextField
            fullWidth
            size='small'
            label='Comment (optional)'
            placeholder='Reason for re-applying...'
            value={reapplyComment}
            onChange={(e) => setReapplyComment(e.target.value)}
            inputProps={{ maxLength: HOLD_COMMENT_MAX }}
            helperText={`${reapplyComment.length}/${HOLD_COMMENT_MAX}`}
            disabled={isSubmitting}
            sx={{
              '& .MuiInputBase-input': { fontFamily: 'AtlasGroteskRegular', fontSize: '0.85rem' },
              '& .MuiFormLabel-root': { fontFamily: 'AtlasGroteskRegular', fontSize: '0.85rem' },
              '& .MuiFormHelperText-root': {
                fontFamily: 'AtlasGroteskRegular',
                fontSize: '0.7rem',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={cancelReapply}
            variant='outlined'
            disabled={isSubmitting}
            sx={{
              fontFamily: 'AtlasGroteskRegular',
              textTransform: 'uppercase',
              fontSize: '0.72rem',
              fontWeight: 700,
              borderColor: '#9aa0a6',
              color: '#5f6368',
              borderRadius: 999,
              px: 2.5,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmReapply}
            variant='contained'
            disabled={isSubmitting}
            sx={{
              fontFamily: 'AtlasGroteskRegular',
              textTransform: 'uppercase',
              fontSize: '0.72rem',
              fontWeight: 700,
              backgroundColor: '#1a6b52',
              color: '#fff',
              borderRadius: 999,
              px: 2.5,
              '&:hover': { backgroundColor: '#145a4b' },
              '&.Mui-disabled': { backgroundColor: '#cccccc', color: '#ffffff' },
            }}
          >
            Re-Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth='lg'>
        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Typography sx={{ fontFamily: 'SanomatMedium', fontSize: '2rem', color: '#006a63' }}>
              Holds History
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #a7a7a7',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Select
                value={historyHoldCode}
                onChange={(e) => setHistoryHoldCode(e.target.value)}
                size='small'
                sx={{
                  minWidth: 150,
                  fontFamily: 'AtlasGroteskRegular',
                  '& fieldset': { border: 'none' },
                }}
              >
                <MenuItem value='ALL'>All Hold Codes</MenuItem>
                {historyHoldCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                size='small'
                placeholder='Search'
                value={historyQuery}
                onChange={(e) => setHistoryQuery(e.target.value)}
                sx={{
                  minWidth: 260,
                  '& fieldset': { border: 'none' },
                  '& .MuiInputBase-input': {
                    fontFamily: 'AtlasGroteskRegular',
                    fontSize: '0.9rem',
                  },
                }}
                InputProps={{
                  endAdornment: <SearchIcon sx={{ color: '#222' }} />,
                }}
              />
            </Box>
          </Box>

          <Box sx={{ border: '1px solid #d0d0d0', borderRadius: 1, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '110px 1fr 130px 180px 140px 1fr 220px',
                px: 2,
                py: 1.5,
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #d8d8d8',
                alignItems: 'center',
              }}
            >
              {[
                'Hold Code',
                'Hold Description',
                'Applied To',
                'Time',
                'Hold By',
                'Comment',
                'Hold Type',
              ].map((label) => (
                <Typography
                  key={label}
                  sx={{
                    fontFamily: 'AtlasGroteskRegular',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    color: '#334155',
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>

            {paginatedHistoryRows.length === 0 ? (
              <Box sx={{ px: 2, py: 3 }}>
                <Typography
                  sx={{ fontFamily: 'AtlasGroteskRegular', fontSize: '0.9rem', color: '#4b5563' }}
                >
                  No hold history entries found.
                </Typography>
              </Box>
            ) : (
              paginatedHistoryRows.map((row) => (
                <Box
                  key={row.key}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '110px 1fr 130px 180px 140px 1fr 220px',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid #ececec',
                    alignItems: 'center',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'AtlasGroteskRegular',
                      fontSize: '0.92rem',
                      color: '#1f2937',
                    }}
                  >
                    {row.holdCode}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'AtlasGroteskRegular',
                      fontSize: '0.92rem',
                      color: '#1f2937',
                    }}
                  >
                    {row.description}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'AtlasGroteskRegular',
                      fontSize: '0.92rem',
                      color: '#1f2937',
                    }}
                  >
                    {row.appliedTo}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'AtlasGroteskRegular',
                      fontSize: '0.92rem',
                      color: '#1f2937',
                    }}
                  >
                    {formatTime(row.time)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'AtlasGroteskRegular',
                      fontSize: '0.92rem',
                      color: '#1f2937',
                    }}
                  >
                    {row.holdBy}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'AtlasGroteskRegular',
                      fontSize: '0.92rem',
                      color: '#1f2937',
                    }}
                  >
                    {row.comment}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Typography
                      sx={{
                        fontFamily: 'AtlasGroteskRegular',
                        fontSize: '0.92rem',
                        color: '#1f2937',
                      }}
                    >
                      {row.holdType}
                    </Typography>
                    {row.holdType === 'Released' && (
                      <Button
                        variant='outlined'
                        size='small'
                        onClick={() => handleReapply(row)}
                        disabled={isSubmitting}
                        sx={{
                          fontFamily: 'AtlasGroteskRegular',
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          borderColor: '#1a8a6a',
                          color: '#1a8a6a',
                          borderRadius: 999,
                          px: 1.5,
                          py: 0.25,
                          minHeight: 26,
                          '&:hover': {
                            borderColor: '#145a4b',
                            color: '#145a4b',
                            backgroundColor: '#f0faf5',
                          },
                        }}
                      >
                        Re-Apply
                      </Button>
                    )}
                  </Box>
                </Box>
              ))
            )}

            {/* Pagination footer */}
            {totalHistoryRows > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  backgroundColor: '#f8f8f8',
                  borderTop: '1px solid #e7e7e7',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{ fontFamily: 'AtlasGroteskRegular', fontSize: '0.82rem', color: '#555' }}
                  >
                    Rows per page:
                  </Typography>
                  <Select
                    size='small'
                    value={historyRowsPerPage}
                    onChange={(e) => {
                      setHistoryRowsPerPage(Number(e.target.value))
                      setHistoryPage(0)
                    }}
                    sx={{
                      fontFamily: 'AtlasGroteskRegular',
                      fontSize: '0.82rem',
                      height: 28,
                      '& .MuiSelect-select': { py: 0.25, pl: 1, pr: 3 },
                    }}
                  >
                    {[2, 5, 10, 25].map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{ fontFamily: 'AtlasGroteskRegular', fontSize: '0.82rem', color: '#555' }}
                  >
                    {`Showing ${pageStart + 1}-${pageEnd} of ${totalHistoryRows} rows`}
                  </Typography>
                  <IconButton
                    size='small'
                    disabled={safePage === 0}
                    onClick={() => setHistoryPage(0)}
                    sx={{ color: '#555' }}
                  >
                    <FirstPageIcon fontSize='small' />
                  </IconButton>
                  <IconButton
                    size='small'
                    disabled={safePage === 0}
                    onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                    sx={{ color: '#555' }}
                  >
                    <ChevronLeftIcon fontSize='small' />
                  </IconButton>
                  <IconButton
                    size='small'
                    disabled={safePage >= totalHistoryPages - 1}
                    onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages - 1, p + 1))}
                    sx={{ color: '#555' }}
                  >
                    <ChevronRightIcon fontSize='small' />
                  </IconButton>
                  <IconButton
                    size='small'
                    disabled={safePage >= totalHistoryPages - 1}
                    onClick={() => setHistoryPage(totalHistoryPages - 1)}
                    sx={{ color: '#555' }}
                  >
                    <LastPageIcon fontSize='small' />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setHistoryOpen(false)}
            variant='outlined'
            sx={{
              fontFamily: 'AtlasGroteskRegular',
              textTransform: 'uppercase',
              borderColor: '#2db47d',
              color: '#2db47d',
              borderRadius: 999,
              px: 3,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

HoldDetailsTableV2.propTypes = {
  holds: PropTypes.array.isRequired,
  onRelease: PropTypes.func.isRequired,
  onReleaseAll: PropTypes.func.isRequired,
  onReapply: PropTypes.func,
  isSubmitting: PropTypes.bool.isRequired,
}

export default HoldDetailsTableV2
